import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";

import Stepper from "./components/Stepper";
import { CompanyForm, OTPVerification } from "./Registration";
import Login from "./Login";
import messages from "../../assets/locale/messages";
import { AUTH_CONTEXT_TYPE } from "../../utils/authPortal";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import {
  getCompanyLoginCanonicalStepRoute,
  getResolvedCompanyPortalSession,
  isAllowedCompanyLoginStepPath,
  isCompanyLoginPath,
  isCompanyRegistrationIncompleteState,
  normalizeCompanyLoginPathname,
  persistCompanyPortalSession,
} from "../../utils/companyRegistration";

import "./Registration/RegistrationFlow.scss";

const formatStepProgressLabel = (template, stepDisplay, totalDisplay) =>
  template.replace("{step}", stepDisplay).replace("{total}", totalDisplay);

/**
 * Company portal login shell: credentials, then optional OTP / company steps
 * under `/company/login/*` (same step components as registration).
 */
const CompanyLoginFlow = ({ type = AUTH_CONTEXT_TYPE.COMPANY }) => {
  const history = useHistory();
  const location = useLocation();
  const loginState = useSelector((state) => state.auth.login);
  const lang = useSelector((state) => state.locale.lang);
  const { registrationFlow: rf } = messages[lang];

  const loginPath = useMemo(
    () => normalizeCompanyLoginPathname(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!isCompanyLoginPath(location.pathname)) return;
    if (location.pathname !== loginPath) {
      history.replace(`${loginPath}${location.search}${location.hash}`);
    }
  }, [location.pathname, loginPath, history, location.search, location.hash]);

  useEffect(() => {
    if (!isCompanyLoginPath(loginPath)) return;
    const target = getCompanyLoginCanonicalStepRoute();
    if (target === ROUTE_PATHS.companyHome) {
      const a = getResolvedCompanyPortalSession();
      if (a?.auth_token && !isCompanyRegistrationIncompleteState(a)) {
        history.replace(ROUTE_PATHS.companyHome);
      }
      return;
    }
    if (isAllowedCompanyLoginStepPath(loginPath)) {
      return;
    }
    if (loginPath !== target) {
      history.replace(target);
    }
  }, [loginPath, loginState, history]);

  const loginFormStepPaths = useMemo(() => {
    if (
      !loginState?.auth_token ||
      !isCompanyRegistrationIncompleteState(loginState)
    ) {
      return [];
    }
    if (loginState.profile_status === "unverified") {
      return [ROUTE_PATHS.companyLoginOtp, ROUTE_PATHS.companyLoginCompany];
    }
    if (
      loginState.profile_status === "verified" &&
      (loginState.company == null || loginState.company === undefined)
    ) {
      return [ROUTE_PATHS.companyLoginCompany];
    }
    return [];
  }, [loginState]);

  const formProgressIndex = useMemo(() => {
    const i = loginFormStepPaths.indexOf(loginPath);
    return i;
  }, [loginFormStepPaths, loginPath]);

  const showFormProgress =
    formProgressIndex >= 0 && loginFormStepPaths.length > 0;

  const stepProgressLabel = useMemo(() => {
    if (!showFormProgress) return "";
    const stepNum = formProgressIndex + 1;
    const total = loginFormStepPaths.length;
    return formatStepProgressLabel(
      rf.stepProgress,
      String(stepNum).padStart(2, "0"),
      String(total).padStart(2, "0"),
    );
  }, [showFormProgress, formProgressIndex, loginFormStepPaths.length, rf.stepProgress]);

  const handleOtpVerified = (data) => {
    persistCompanyPortalSession(data);
    if (isCompanyRegistrationIncompleteState(data)) {
      history.push(ROUTE_PATHS.companyLoginCompany);
    } else {
      history.replace(ROUTE_PATHS.companyHome);
    }
  };

  const handleCompanyComplete = (data) => {
    persistCompanyPortalSession(data);
    history.push(ROUTE_PATHS.companyPendingApproval);
  };

  return (
    <div className="registration-flow" data-auth-portal={type}>
      {showFormProgress && (
        <Stepper
          currentStep={formProgressIndex + 1}
          totalSteps={loginFormStepPaths.length}
          label={stepProgressLabel}
        />
      )}

      <div>
        <Switch>
          <Route path={ROUTE_PATHS.companyLogin} exact>
            <Login type={type} />
          </Route>
          <Route path={ROUTE_PATHS.companyLoginOtp} exact>
            <OTPVerification onSubmit={handleOtpVerified} />
          </Route>
          <Route path={ROUTE_PATHS.companyLoginCompany} exact>
            <CompanyForm onComplete={handleCompanyComplete} />
          </Route>
          <Redirect to={ROUTE_PATHS.companyLogin} />
        </Switch>
      </div>
    </div>
  );
};

CompanyLoginFlow.propTypes = {
  type: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

export default CompanyLoginFlow;
